/**
 * MODULE: _shared/frontend-shell/src/components/brain/BrainLearningStatusBanner.tsx
 * PURPOSE: Shared self-learning status banner for Settings, assistant rail, CRM, Pages, and dashboard [2.2.1244–1245].
 * PUBLIC_API: BrainLearningStatusBanner, shouldShowBrainLearningStatusBanner (via brainMemoryApi)
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-05-26 [2.2.1245] — idle onboarding slate styling + `learning_status.next_step` hint line; label **Suggested** (not narrative step wording) [2.2.1256].
 */

import type { BrainUserLearningStatus } from '@shared/lib/frontend/api/brainMemoryApi';
import { brainLearnedPreferencesSettingsPath } from '@shared/lib/frontend/utils/brainInsightLinks';
import { Link } from 'react-router-dom';

export type BrainLearningStatusBannerProps = {
  status: BrainUserLearningStatus;
  className?: string;
  testId?: string;
  /** When true, show a link to Settings → Learned preferences for non-idle states. */
  showSettingsLink?: boolean;
  /** Tap-to-ask coaching / learning next step (dock and dashboard strips) [2.2.1315]. */
  onNextStepClick?: (step: string) => void;
};

export function BrainLearningStatusBanner({
  status,
  className = '',
  testId = 'brain-learning-status-banner',
  showSettingsLink = false,
  onNextStepClick,
}: BrainLearningStatusBannerProps) {
  const state = status.state || 'idle';
  const isIdle = state === 'idle';
  const isCatchUp = state === 'catching_up' || status.catch_up_delayed;
  const border = isCatchUp
    ? 'border-amber-200 bg-amber-50/80 dark:border-amber-900/40 dark:bg-amber-950/25'
    : isIdle
      ? 'border-slate-200 bg-slate-50/80 dark:border-slate-700/50 dark:bg-slate-900/30'
      : 'border-violet-200 bg-violet-50/80 dark:border-violet-900/40 dark:bg-violet-950/25';
  const titleColor = isCatchUp
    ? 'text-amber-900 dark:text-amber-100'
    : isIdle
      ? 'text-slate-800 dark:text-slate-100'
      : 'text-violet-900 dark:text-violet-100';
  const bodyColor = isCatchUp
    ? 'text-amber-800/90 dark:text-amber-100/90'
    : isIdle
      ? 'text-slate-700/90 dark:text-slate-200/90'
      : 'text-violet-800/90 dark:text-violet-100/90';

  return (
    <div
      className={`rounded-lg border p-3 ${border} ${className}`}
      data-testid={testId}
      role="status"
    >
      <p className={`text-sm font-semibold ${titleColor}`}>{status.headline}</p>
      {status.detail ? <p className={`mt-1 text-xs ${bodyColor}`}>{status.detail}</p> : null}
      {status.next_step ? (
        onNextStepClick ? (
          <button
            type="button"
            className={`mt-1.5 inline-flex max-w-full rounded-full border border-current/20 bg-white/70 px-2.5 py-1 text-left text-[11px] font-medium transition hover:bg-white dark:bg-black/20 dark:hover:bg-black/30 ${bodyColor}`}
            data-testid={`${testId}-next-step-action`}
            onClick={() => onNextStepClick(status.next_step!.trim())}
          >
            <span className="truncate">Suggested: {status.next_step}</span>
          </button>
        ) : (
          <p
            className={`mt-1.5 text-[11px] font-medium ${bodyColor}`}
            data-testid={`${testId}-next-step`}
          >
            Suggested: {status.next_step}
          </p>
        )
      ) : null}
      {showSettingsLink && state !== 'catching_up' ? (
        <Link
          to={brainLearnedPreferencesSettingsPath()}
          className={`mt-2 inline-block text-[11px] font-medium underline decoration-violet-400/60 underline-offset-2 hover:opacity-90 ${bodyColor}`}
          data-testid={`${testId}-settings-link`}
        >
          View learning status in Settings
        </Link>
      ) : null}
    </div>
  );
}
